import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Alert, Card, CardBody, Col, Container, Row } from 'reactstrap';

import ButtonLink from '../../components/common/ButtonLink';
import HelperNavigation from '../../components/common/HelperNavigation';
import PageTitle from '../../components/common/PageTitle';
import ScrollReset from '../../components/common/ScrollReset';
import AlternateLink from '../../components/head/AlternateLink';
import CanonicalLink from '../../components/head/CanonicalLink';
import SEOTitle from '../../components/head/SEOTitle';
import Page from '../../components/layout/Page';
import Spinner from '../../components/loader/Spinner';
import LastVisitedProductsSlider from '../../components/slider/LastVisitedProductsSlider';
import WishListEmailForm from '../../components/wish-list/WishListEmailForm';
import WishListProductRow from '../../components/wish-list/WishListProductRow';
import globalTranslations from '../../i18n/globalTranslations';
import { modelOf } from '../../prop-types';
import RouteService from '../../services/RouteService';
import AccountStore from '../../store/AccountStore';
import ConfigStore from '../../store/ConfigStore';
import ProductStore from '../../store/ProductStore';
import UIStore from '../../store/UIStore';
import WishlistStore from '../../store/WishlistStore';
import AccountModalTab from '../../types/AccountModalTab';
import CommonPage from '../../types/CommonPage';
import Paths from '../../types/Paths';
import ProductClass from '../../types/ProductClass';
import RequestState from '../../types/RequestState';
import { urlParams } from '../../util/constants';

const MobileWishlistLabels = {
  name: <FormattedMessage id="wishlist.Product" defaultMessage="Product" />,
  quantity: <FormattedMessage {...globalTranslations.amountAndInTotal} />,
  unitPrice: <FormattedMessage {...globalTranslations.unitPrice} />,
};

@observer
class WishListPage extends Component {
  constructor(props) {
    super(props);
    this.redirectIfWishlistIsNotEnabled();

    this.state = {
      sharedStorage: [],
      notFound: false,
      alertVisible: true,
    };
  }

  redirectIfWishlistIsNotEnabled = () => {
    const { configStore, routeService, history } = this.props;
    if (!configStore.wishlist.enabled) {
      history.push(routeService.getPath(Paths.NotFoundPage));
    }
  };

  componentDidMount() {
    const { accessToken } = this.props.match.params;
    const { productStore } = this.props;

    if (accessToken) {
      productStore
        .getProductByAccessToken({ accessToken })
        .then((response) => {
          this.setState({ sharedStorage: response.data });
        })
        .catch(() => {
          this.setState({ notFound: true });
        });
    }
  }

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.wishListTitle),
        url: Paths.WishList,
      },
    ]);
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4,
      },
      sizes
    );
    return (
      <Col className="WishListPage__product-list-header" {...actualSizes}>
        <label className="WishListPage__product-list-header-label">
          {label}
        </label>
      </Col>
    );
  };

  productListNotFoundAlertBanner = () => {
    if (!this.state.notFound) {
      return null;
    }

    const message = (
      <FormattedMessage
        id="wishlist.wishlistNotFound"
        defaultMessage="Wishlist doesn't exist with the given id."
      />
    );
    return this.renderAlertBanner('', message, 'warning');
  };

  emptyProductList = () => {
    if (
      this.getStorage().length ||
      this.props.match.params.accessToken ||
      this.isWishlistHydrating()
    ) {
      return null;
    }
    const message = (
      <FormattedMessage
        id="wishlist.wishlistIsEmpty"
        defaultMessage="Wishlist is empty."
      />
    );
    return this.renderAlertBanner('', message, 'secondary');
  };

  productInfoLabelRow = () => {
    const { uiStore } = this.props;
    if (!uiStore.isDesktop || !this.getStorage().length) {
      return null;
    }
    return (
      <Row className="WishListPage__product-list--headers">
        {this.renderProductHeaderCol(MobileWishlistLabels.name, {
          md: 4,
        })}
        {this.renderProductHeaderCol(MobileWishlistLabels.unitPrice, {
          sm: 2,
        })}
        {this.renderProductHeaderCol(MobileWishlistLabels.quantity, {
          sm: 2,
          md: 4,
        })}
      </Row>
    );
  };

  updateStorage = (updatedProduct) => {
    const { wishlistStore } = this.props;
    wishlistStore.addOrUpdateItem(updatedProduct);
  };

  deleteProduct = (product) => {
    const { wishlistStore } = this.props;
    wishlistStore.removeItem(product);
  };

  getStorage = () => {
    const { wishlistStore, match } = this.props;
    return match.params.accessToken
      ? this.state.sharedStorage
      : wishlistStore.items;
  };

  hasSharedAccessToken = () => {
    const { location, match } = this.props;
    const searchParams = new URLSearchParams(location.search);

    return (
      !!match.params.accessToken || searchParams.has(urlParams.accessToken)
    );
  };

  isWishlistHydrating = () => {
    const { accountStore, wishlistStore } = this.props;

    return (
      accountStore.loggedIn &&
      !this.hasSharedAccessToken() &&
      wishlistStore.hydrateState === RequestState.LOADING
    );
  };

  renderWishList = () => {
    const storage = this.getStorage();
    const { match, wishlistStore } = this.props;

    if (storage) {
      return storage.map((product) => (
        <Fragment key={this.getProductIdByProductClass(product)}>
          <div className="WishListPage__product-row-divider" />
          <WishListProductRow
            product={product}
            match={match}
            onStorageUpdate={this.updateStorage}
            onProductDelete={this.deleteProduct}
            disableActions={wishlistStore.isRowMutating(
              product.extendedId || product.id
            )}
          />
        </Fragment>
      ));
    }
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI ||
      product.class === ProductClass.COLLECTION
      ? product.id
      : product.product_id;
  };

  accountLoginButtonLink = () => (
    <ButtonLink onClick={this.toggle.bind(this, AccountModalTab.LOGIN)}>
      <u>
        <FormattedMessage id="wishlist.login" defaultMessage="login" />
      </u>
    </ButtonLink>
  );

  notLoggedInAlertBanner = () => {
    const { accountStore, configStore } = this.props;
    if (accountStore.loggedIn || !this.getStorage().length) {
      return null;
    }

    if (configStore.registration.enabled && !accountStore.loggedIn) {
      return this.alertWithRegistrationLink();
    } else {
      const message = (
        <FormattedMessage
          id="wishlist.NotLoggedInBanner"
          defaultMessage="You're not yet logged in. You have to {login_link} as a customer to send the list as an email or save it permanently."
          values={{
            login_link: (
              <ButtonLink
                onClick={this.toggle.bind(this, AccountModalTab.LOGIN)}
              >
                <u>
                  <FormattedMessage
                    id="wishlist.login"
                    defaultMessage="login"
                  />
                </u>
              </ButtonLink>
            ),
          }}
        />
      );
      return this.renderAlertBanner('', message, 'warning', true);
    }
  };

  alertWithRegistrationLink = () => {
    const message = (
      <FormattedMessage
        id="wishlist.NotLoggedInBanner_Registration_Enabled"
        defaultMessage={
          "You're not yet logged in. You have to {login_link} or {register_link} as a customer to send the list as an email or save it permanently."
        }
        values={{
          login_link: this.accountLoginButtonLink(),
          register_link: (
            <ButtonLink
              onClick={this.toggle.bind(this, AccountModalTab.REGISTER)}
            >
              <u>
                <FormattedMessage
                  id="wishlist.register"
                  defaultMessage="register"
                />
              </u>
            </ButtonLink>
          ),
        }}
      />
    );
    return this.renderAlertBanner('', message, 'warning', true);
  };

  onAlertDismiss = () => {
    this.setState({
      alertVisible: false,
    });
  };

  toggle = (tab) => {
    const { accountStore } = this.props;
    accountStore.accountModal.setTab(tab);
    accountStore.accountModal.toggle();
  };

  emailWishlistForm = () => {
    const { accountStore, configStore, match } = this.props;
    const { notFound } = this.state;
    const storage = this.getStorage();
    if (
      !accountStore.loggedIn ||
      !configStore.wishlist.enableEmailShare ||
      notFound ||
      match.params.accessToken
    ) {
      return null;
    }

    if (storage.length) {
      return <WishListEmailForm />;
    }
  };

  productListInformationAlertBanner = () => {
    const { accountStore, match } = this.props;
    const { notFound } = this.state;
    const storage = this.getStorage();
    if (
      !accountStore.loggedIn ||
      !storage.length ||
      notFound ||
      match.params.accessToken
    ) {
      return null;
    }

    const message = (
      <FormattedMessage
        id="wishlist.ProductListEmailInformation"
        defaultMessage="You can add products to the list and make order decision later. You can also send the list as an email of your choosing."
      />
    );
    return this.renderAlertBanner(
      'WishListPage__product-list-information-alert-banner',
      message,
      'secondary'
    );
  };

  renderAlertBanner = (className, formattedMessage, color, dismissable) => (
    <Alert
      className={classNames(className)}
      color={color}
      isOpen={this.state.alertVisible}
      toggle={dismissable && this.onAlertDismiss}
    >
      {formattedMessage}
    </Alert>
  );

  wishlistHydrateIndicator = () => {
    if (!this.isWishlistHydrating() || this.getStorage().length) {
      return null;
    }

    return (
      <div
        className="WishListPage__hydrate-indicator"
        role="status"
        aria-live="polite"
      >
        <Spinner />
        <p className="WishListPage__hydrate-indicator-text">
          <FormattedMessage {...globalTranslations.loading} />
        </p>
      </div>
    );
  };

  renderWishListContent = () => {
    const emailWishlistForm = this.emailWishlistForm();
    const hasEmailWishlistForm = !!emailWishlistForm;

    return (
      <Row className="WishListPage__card-row">
        <Col xs={12} xl={hasEmailWishlistForm ? 9 : 12}>
          <Card className="WishListPage__product-list-card">
            <CardBody
              className="WishListPage__product-list-card-body"
              aria-busy={this.isWishlistHydrating()}
            >
              {this.notLoggedInAlertBanner()}
              {this.productListInformationAlertBanner()}
              {this.productListNotFoundAlertBanner()}
              {this.wishlistHydrateIndicator()}
              {this.emptyProductList()}
              {this.productInfoLabelRow()}
              {this.renderWishList()}
            </CardBody>
          </Card>
        </Col>
        {hasEmailWishlistForm ? (
          <Col xs={12} xl={3}>
            <Card className="WishListPage__product-list-card">
              <CardBody className="WishListPage__product-list-card-body-email-form">
                {emailWishlistForm}
              </CardBody>
            </Card>
          </Col>
        ) : null}
      </Row>
    );
  };

  render() {
    const { routeService } = this.props;

    return (
      <Page name={CommonPage.WISH_LIST} className="WishListPage">
        <SEOTitle title={globalTranslations.wishListTitle} />
        <ScrollReset />
        <Container className="WishListPage__container">
          <Row>
            <Col xs={12}>
              <CanonicalLink path={routeService.getPath(Paths.WishList)} />
              <AlternateLink path={Paths.WishList} />
              <div className="WishListPage__HelperNavigation">
                <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
              </div>
              <PageTitle>
                <FormattedMessage {...globalTranslations.wishListTitle} />
              </PageTitle>
            </Col>
          </Row>
          {this.renderWishListContent()}
          <LastVisitedProductsSlider />
        </Container>
      </Page>
    );
  }
}

WishListPage.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  wishlistStore: modelOf(WishlistStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  location: RouterPropTypes.location.isRequired,
};

export default injectIntl(
  withRouter(
    inject(
      'accountStore',
      'routeService',
      'productStore',
      'configStore',
      'uiStore',
      'wishlistStore'
    )(WishListPage)
  )
);
